/**
 * Token-match-only filter — a pure tier-5 path used by the recap product
 * picker as a fast alternative to the full {@link tieredSearch} when the
 * query is the exact name of an already-existing seed (so tokens are
 * guaranteed to be present in the catalog).
 *
 * No Fuse, no fuzzy/typo tolerance, no number normalization. Roughly 5-10×
 * faster than the full tiered ranker because it skips the Fuse pass.
 */
import { tokenize, tokenMatchScore } from "./tieredSearch"

export interface TokenMatchConfig<T> {
  /** Extract all text fields whose words contribute to matching. */
  textFields: (item: T) => Array<string | null | undefined>
}

export interface TokenMatchResult<T> {
  item: T
  score: number
}

/**
 * Score items by token overlap with the query (+2 exact word, +1 prefix
 * word ≥2 chars). Returns those with score > 0, sorted score-desc.
 */
export function tokenMatchFilter<T>(
  items: T[],
  query: string,
  config: TokenMatchConfig<T>,
  tiebreaker?: (a: T, b: T) => number
): TokenMatchResult<T>[] {
  // Same size-grade-aware tokenization as the full tiered ranker, so the recap
  // picker's fast path matches identically (see tokenize in tieredSearch).
  const queryWords = tokenize(query)
  if (queryWords.length === 0) return []

  const scored: TokenMatchResult<T>[] = []
  for (const item of items) {
    const fields = config.textFields(item)
    let text = ""
    for (const f of fields) {
      if (f != null) {
        if (text) text += " "
        text += f.toLowerCase()
      }
    }
    if (!text) continue
    const targetWords = tokenize(text)
    const score = tokenMatchScore(queryWords, targetWords)
    if (score > 0) scored.push({ item, score })
  }

  scored.sort((a, b) => {
    const scoreDiff = b.score - a.score
    if (scoreDiff !== 0) return scoreDiff
    return tiebreaker ? tiebreaker(a.item, b.item) : 0
  })

  return scored
}
